import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Button, Form, InputNumber, Input, Modal, Select, Space, Switch, Table, Tag, Typography, message } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  createSubscriptionPlan,
  deactivateSubscriptionPlan,
  getCreditTypes,
  getSubscriptionPlans,
  reactivateSubscriptionPlan,
  updateSubscriptionPlan,
} from "../redux/adminSlice";
import PlanCoupons from "./PlanCoupons";
import "./Admin.css";

const { Text } = Typography;

const currency = new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" });

const genderOptions = ["Female", "Male", "Both"].map((v) => ({ label: v, value: v }));

const errorText = (err, fallback) =>
  (Array.isArray(err?.errors) && err.errors.join(", ")) || err?.message || fallback;

const SubscriptionPlans = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [plans, setPlans] = useState([]);
  const [creditTypes, setCreditTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const totalAmount = Form.useWatch("totalAmount", form);
  const noMonths = Form.useWatch("noMonths", form);
  const watchedCredits = Form.useWatch("credits", form);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    const result = await dispatch(getSubscriptionPlans(includeInactive));
    if (getSubscriptionPlans.fulfilled.match(result)) {
      setPlans(Array.isArray(result.payload) ? result.payload : []);
    } else {
      message.error(errorText(result.payload, "Could not load care plans."));
    }
    setLoading(false);
  }, [dispatch, includeInactive]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    dispatch(getCreditTypes()).then((result) => {
      if (getCreditTypes.fulfilled.match(result)) {
        setCreditTypes(Array.isArray(result.payload) ? result.payload : []);
      } else {
        message.error(errorText(result.payload, "Could not load credit types."));
      }
    });
  }, [dispatch]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ credits: [{}], allowInstallments: false });
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    form.resetFields();
    form.setFieldsValue({
      subscriptionName: plan.subscriptionName,
      gender: plan.gender,
      description: plan.description,
      allowInstallments: !!plan.allowInstallments,
      totalAmount: plan.totalAmount,
      noMonths: plan.noMonths,
      credits: (plan.credits || []).map((c) => ({ creditTypeId: c.creditTypeId, points: c.points })),
    });
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (values) => {
    const body = {
      subscriptionName: values.subscriptionName.trim(),
      gender: values.gender,
      description: (values.description || "").trim(),
      allowInstallments: !!values.allowInstallments,
      monthlyAmount: Math.round((values.totalAmount * 100) / values.noMonths) / 100,
      noMonths: values.noMonths,
      credits: values.credits.map((c) => ({ creditTypeId: c.creditTypeId, points: c.points })),
    };

    setSubmitting(true);
    const thunk = editing ? updateSubscriptionPlan : createSubscriptionPlan;
    const result = await dispatch(thunk(editing ? { subscriptionId: editing.subscriptionId, ...body } : body));
    setSubmitting(false);

    if (thunk.rejected.match(result)) {
      message.error(errorText(result.payload, "Could not save the plan. Please try again."));
      return;
    }
    message.success(result.payload?.message || "Plan saved.");
    closeModal();
    loadPlans();
  };

  const runAction = async (thunk, plan, fallback) => {
    const result = await dispatch(thunk(plan.subscriptionId));
    if (thunk.rejected.match(result)) {
      message.error(errorText(result.payload, fallback));
      return;
    }
    message.success(result.payload?.message || "Done.");
    loadPlans();
  };

  const confirmDeactivate = (plan) =>
    Modal.confirm({
      title: `Deactivate "${plan.subscriptionName}"?`,
      content: (
        <>
          <p>The plan will be hidden from patients and archived in Stripe.</p>
          <p>{plan.activeSubscriberCount} active subscriber(s) keep their plan and points.</p>
          {plan.pendingPaymentCount > 0 && (
            <p>
              <Text type="danger">
                {plan.pendingPaymentCount} unpaid payment link(s) for this plan can no longer be paid.
              </Text>
            </p>
          )}
        </>
      ),
      okText: "Deactivate",
      okButtonProps: { danger: true },
      onOk: () => runAction(deactivateSubscriptionPlan, plan, "Could not deactivate the plan."),
    });

  const columns = [
    {
      title: "Name",
      dataIndex: "subscriptionName",
      render: (name, plan) => (
        <>
          {name} {plan.allowInstallments && <Tag color="pink">Klarna</Tag>}
        </>
      ),
    },
    { title: "Gender", dataIndex: "gender" },
    { title: "Monthly", dataIndex: "monthlyAmount", render: (v) => currency.format(v) },
    { title: "Months", dataIndex: "noMonths" },
    { title: "Total", dataIndex: "totalAmount", render: (v) => currency.format(v) },
    {
      title: "Credits",
      dataIndex: "credits",
      render: (credits) =>
        (credits || []).map((c) => (
          <Tag key={c.creditTypeId}>
            {c.name} × {c.points}
          </Tag>
        )),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (active) => <Tag color={active ? "green" : "default"}>{active ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Active coupons",
      dataIndex: "coupons",
      render: (coupons) => (coupons || []).filter((c) => c.isActive && !c.isExpired).length,
    },
    { title: "Subscribers", dataIndex: "activeSubscriberCount" },
    { title: "Pending payments", dataIndex: "pendingPaymentCount" },
    {
      title: "",
      key: "actions",
      render: (_, plan) => (
        <Space>
          <Button size="small" onClick={() => openEdit(plan)}>
            Edit
          </Button>
          {plan.isActive && !plan.isProtected && (
            <Button size="small" danger onClick={() => confirmDeactivate(plan)}>
              Deactivate
            </Button>
          )}
          {!plan.isActive && (
            <Button
              size="small"
              onClick={() => runAction(reactivateSubscriptionPlan, plan, "Could not reactivate the plan.")}
            >
              Reactivate
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const monthlyAmount =
    Number.isFinite(totalAmount) && Number.isFinite(noMonths) && noMonths > 0
      ? Math.round((totalAmount * 100) / noMonths) / 100
      : null;
  const priceChanged =
    editing && (totalAmount !== editing.totalAmount || noMonths !== editing.noMonths);
  const selectedTypeIds = (watchedCredits || []).map((c) => c?.creditTypeId);

  const validateName = (_, value) => {
    const name = String(value || "").trim().toLowerCase();
    const clash = plans.some(
      (p) => p.isActive && p.subscriptionId !== editing?.subscriptionId && p.subscriptionName.trim().toLowerCase() === name,
    );
    return clash ? Promise.reject(new Error("Another active plan already uses this name")) : Promise.resolve();
  };

  return (
    <div style={{ margin: "2rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <p style={{ color: "#335CAD", fontWeight: "bold", fontSize: 16, margin: 0 }}>Care Plans</p>
        <Space>
          <span>
            Show inactive <Switch checked={includeInactive} onChange={setIncludeInactive} />
          </span>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: "#00ADEF", borderColor: "#00ADEF" }}
            onClick={openCreate}
          >
            Create plan
          </Button>
        </Space>
      </div>

      <Table
        rowKey="subscriptionId"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={plans}
        expandable={{ expandedRowRender: (plan) => <PlanCoupons plan={plan} onChanged={loadPlans} /> }}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        scroll={{ x: true }}
      />

      <Modal
        open={modalOpen}
        title={editing ? `Edit "${editing.subscriptionName}"` : "Create care plan"}
        okText={editing ? "Save changes" : "Create plan"}
        confirmLoading={submitting}
        onOk={() => form.submit()}
        onCancel={closeModal}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="subscriptionName"
            label="Plan name"
            rules={[
              { required: true, message: "Please enter a plan name" },
              { whitespace: true, message: "Plan name cannot be empty" },
              { max: 200, message: "Plan name can be at most 200 characters" },
              { validator: validateName },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="gender"
            label="Available to"
            rules={[{ required: true, message: "Select who can see this plan" }]}
          >
            <Select placeholder="Female, Male or Both" options={genderOptions} style={{ width: 240 }} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (optional)"
            rules={[{ max: 1000, message: "Description can be at most 1000 characters" }]}
          >
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>

          <Form.Item
            name="allowInstallments"
            label="Allow pay later (Klarna)"
            valuePropName="checked"
            extra="Patients can split the payment with Klarna. The clinic still receives the full amount."
          >
            <Switch />
          </Form.Item>

          <Space align="start" size={16}>
            <Form.Item
              name="totalAmount"
              label="Total amount (CAD, tax included)"
              dependencies={["noMonths"]}
              rules={[
                { required: true, message: "Enter an amount" },
                { type: "number", min: 0.01, message: "Must be greater than 0" },
                {
                  validator: (_, value) =>
                    !value || !noMonths || Math.round(value * 100) % noMonths === 0
                      ? Promise.resolve()
                      : Promise.reject(new Error("Total must divide evenly into cents per month")),
                },
              ]}
            >
              <InputNumber min={0} precision={2} style={{ width: 180 }} />
            </Form.Item>
            <Form.Item
              name="noMonths"
              label="Number of months"
              rules={[{ required: true, message: "Enter 1 to 60" }]}
            >
              <InputNumber min={1} max={60} precision={0} style={{ width: 140 }} />
            </Form.Item>
          </Space>

          <p>
            Monthly amount: <Text strong>{monthlyAmount === null ? "-" : currency.format(monthlyAmount)}</Text> x{" "}
            {noMonths || "-"} months, paid once
          </p>

          <Form.List
            name="credits"
            rules={[
              {
                validator: (_, credits) =>
                  credits?.length ? Promise.resolve() : Promise.reject(new Error("Add at least one credit type")),
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                <Text strong>Credits (points per appointment type)</Text>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" style={{ display: "flex", marginTop: 8 }}>
                    <Form.Item
                      name={[field.name, "creditTypeId"]}
                      rules={[{ required: true, message: "Select a credit type" }]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder="Credit type"
                        style={{ width: 240 }}
                        options={creditTypes.map((t) => ({
                          value: t.id,
                          label: t.name,
                          disabled:
                            selectedTypeIds.includes(t.id) && selectedTypeIds[field.name] !== t.id,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, "points"]}
                      rules={[
                        { required: true, message: "Enter points" },
                        { type: "number", min: 0.01, message: "Must be greater than 0" },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber min={0} precision={2} placeholder="Points" style={{ width: 140 }} />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Form.ErrorList errors={errors} />
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  style={{ marginTop: 8 }}
                  disabled={fields.length >= creditTypes.length}
                  onClick={() => add()}
                >
                  Add credit type
                </Button>
              </>
            )}
          </Form.List>

          {editing && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message={
                priceChanged
                  ? "Changing the amount or months creates a new Stripe price and archives the old one. Existing buyers keep their points."
                  : "Existing buyers keep their points. Removing a credit type removes it for new purchases only."
              }
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;
